import { io, Socket } from 'socket.io-client'
import { ref, reactive } from 'vue'
import { logger } from '@/utils/logger'

/** Base URL for socket (same host as API). Uses VITE_API_BASE_URL origin or current window origin. */
function getSocketBaseUrl(): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL
  if (!apiBase) {
    return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5001'
  }
  if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
    return new URL(apiBase).origin
  }
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5001'
}

export interface AuctionBid {
  id: string
  auctionId: string
  bidderId: string
  amount: number
  currency: string
  status: string
  createdAt: string
  bidder?: {
    id: string
    username: string
    email: string
  }
}

export interface AuctionEvent {
  auctionId: string
  timestamp: string
  [key: string]: unknown
}

export interface BidOutbidEvent extends AuctionEvent {
  newHighestBid: AuctionBid
}

export interface AuctionEndedEvent extends AuctionEvent {
  winnerId: string
  finalBid: AuctionBid
}

export interface BuyNowCompletedEvent extends AuctionEvent {
  buyerId: string
  buyNowPrice: number
}

export interface TimeWarningEvent extends AuctionEvent {
  timeRemaining: number
}

class AuctionSocketService {
  private socket: Socket | null = null
  private isConnected = ref(false)
  private token: string | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000

  // Event handlers
  private eventHandlers = reactive({
    onNewBid: [] as Array<(bid: AuctionBid) => void>,
    onBidOutbid: [] as Array<(event: BidOutbidEvent) => void>,
    onAuctionEnded: [] as Array<(event: AuctionEndedEvent) => void>,
    onAuctionCancelled: [] as Array<(event: AuctionEvent) => void>,
    onAuctionStatusUpdate: [] as Array<(event: AuctionEvent) => void>,
    onBuyNowCompleted: [] as Array<(event: BuyNowCompletedEvent) => void>,
    onTimeWarning: [] as Array<(event: TimeWarningEvent) => void>,
  })

  private connect() {
    if (!this.token) return

    // Avoid creating multiple parallel socket connections
    if (this.socket) {
      if (this.socket.connected || this.socket.connected === false /* connecting state */) {
        return
      }

      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    try {
      this.socket = io(`${getSocketBaseUrl()}/auctions`, {
        auth: {
          token: this.token,
        },
        transports: ['websocket', 'polling'],
        // Let socket.io handle reconnection instead of our manual backoff logic
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
      })

      this.setupEventListeners()
    } catch (error) {
      logger.error('Failed to connect to auction WebSocket:', error)
    }
  }

  private setupEventListeners() {
    if (!this.socket) return

    this.socket.on('connect', () => {
      logger.debug('Connected to auction WebSocket')
      this.isConnected.value = true
      this.reconnectAttempts = 0
    })

    this.socket.on('disconnect', () => {
      logger.debug('Disconnected from auction WebSocket')
      this.isConnected.value = false
    })

    this.socket.on('connect_error', (error) => {
      logger.error('WebSocket connection error:', error)
      this.isConnected.value = false
    })

    // Auction events
    this.socket.on('new_bid', (data: { auctionId: string; bid: AuctionBid; timestamp: string }) => {
      logger.debug('New bid received:', data)
      this.eventHandlers.onNewBid.forEach(handler => handler(data.bid))
    })

    this.socket.on('bid_outbid', (data: BidOutbidEvent) => {
      logger.debug('Bid outbid:', data)
      this.eventHandlers.onBidOutbid.forEach(handler => handler(data))
    })

    this.socket.on('auction_ended', (data: AuctionEndedEvent) => {
      logger.debug('Auction ended:', data)
      this.eventHandlers.onAuctionEnded.forEach(handler => handler(data))
    })

    this.socket.on('auction_cancelled', (data: AuctionEvent) => {
      logger.debug('Auction cancelled:', data)
      this.eventHandlers.onAuctionCancelled.forEach(handler => handler(data))
    })

    this.socket.on('auction_status_update', (data: AuctionEvent) => {
      logger.debug('Auction status update:', data)
      this.eventHandlers.onAuctionStatusUpdate.forEach(handler => handler(data))
    })

    this.socket.on('buy_now_completed', (data: BuyNowCompletedEvent) => {
      logger.debug('Buy now completed:', data)
      this.eventHandlers.onBuyNowCompleted.forEach(handler => handler(data))
    })

    this.socket.on('time_warning', (data: TimeWarningEvent) => {
      logger.debug('Time warning:', data)
      this.eventHandlers.onTimeWarning.forEach(handler => handler(data))
    })

    // Connection events
    this.socket.on('joined_auction', (data: { auctionId: string }) => {
      logger.debug('Joined auction:', data.auctionId)
    })

    this.socket.on('left_auction', (data: { auctionId: string }) => {
      logger.debug('Left auction:', data.auctionId)
    })

    this.socket.on('error', (error: { message: string }) => {
      logger.error('WebSocket error:', error.message)
    })
  }

  private scheduleReconnect() {
    if (!this.token) return
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('Max reconnection attempts reached')
      return
    }

    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)

    logger.debug(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`)
    
    setTimeout(() => {
      this.connect()
    }, delay)
  }

  setToken(token: string | null) {
    if (token === this.token) return
    this.token = token
    this.reconnectAttempts = 0

    if (!this.token) {
      this.disconnect()
      return
    }

    // Reconnect with new token
    this.disconnect()
    this.connect()
  }

  // Public methods
  joinAuction(auctionId: string) {
    if (this.socket && this.isConnected.value) {
      this.socket.emit('join_auction', { auctionId })
    }
  }

  leaveAuction(auctionId: string) {
    if (this.socket && this.isConnected.value) {
      this.socket.emit('leave_auction', { auctionId })
    }
  }

  // Event subscription methods
  onNewBid(handler: (bid: AuctionBid) => void) {
    this.eventHandlers.onNewBid.push(handler)
    return () => {
      const index = this.eventHandlers.onNewBid.indexOf(handler)
      if (index > -1) {
        this.eventHandlers.onNewBid.splice(index, 1)
      }
    }
  }

  onBidOutbid(handler: (event: BidOutbidEvent) => void) {
    this.eventHandlers.onBidOutbid.push(handler)
    return () => {
      const index = this.eventHandlers.onBidOutbid.indexOf(handler)
      if (index > -1) {
        this.eventHandlers.onBidOutbid.splice(index, 1)
      }
    }
  }

  onAuctionEnded(handler: (event: AuctionEndedEvent) => void) {
    this.eventHandlers.onAuctionEnded.push(handler)
    return () => {
      const index = this.eventHandlers.onAuctionEnded.indexOf(handler)
      if (index > -1) {
        this.eventHandlers.onAuctionEnded.splice(index, 1)
      }
    }
  }

  onAuctionCancelled(handler: (event: AuctionEvent) => void) {
    this.eventHandlers.onAuctionCancelled.push(handler)
    return () => {
      const index = this.eventHandlers.onAuctionCancelled.indexOf(handler)
      if (index > -1) {
        this.eventHandlers.onAuctionCancelled.splice(index, 1)
      }
    }
  }

  onAuctionStatusUpdate(handler: (event: AuctionEvent) => void) {
    this.eventHandlers.onAuctionStatusUpdate.push(handler)
    return () => {
      const index = this.eventHandlers.onAuctionStatusUpdate.indexOf(handler)
      if (index > -1) {
        this.eventHandlers.onAuctionStatusUpdate.splice(index, 1)
      }
    }
  }

  onBuyNowCompleted(handler: (event: BuyNowCompletedEvent) => void) {
    this.eventHandlers.onBuyNowCompleted.push(handler)
    return () => {
      const index = this.eventHandlers.onBuyNowCompleted.indexOf(handler)
      if (index > -1) {
        this.eventHandlers.onBuyNowCompleted.splice(index, 1)
      }
    }
  }

  onTimeWarning(handler: (event: TimeWarningEvent) => void) {
    this.eventHandlers.onTimeWarning.push(handler)
    return () => {
      const index = this.eventHandlers.onTimeWarning.indexOf(handler)
      if (index > -1) {
        this.eventHandlers.onTimeWarning.splice(index, 1)
      }
    }
  }

  // Getters
  get connected() {
    return this.isConnected.value
  }

  get connectedRef() {
    return this.isConnected
  }

  // Cleanup
  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected.value = false
    }
  }
}

// Create singleton instance
export const auctionSocketService = new AuctionSocketService()

// Vue composable for easy use in components
export function useAuctionSocket() {
  return {
    auctionSocket: auctionSocketService,
    connected: auctionSocketService.connectedRef,
    joinAuction: auctionSocketService.joinAuction.bind(auctionSocketService),
    leaveAuction: auctionSocketService.leaveAuction.bind(auctionSocketService),
    onNewBid: auctionSocketService.onNewBid.bind(auctionSocketService),
    onBidOutbid: auctionSocketService.onBidOutbid.bind(auctionSocketService),
    onAuctionEnded: auctionSocketService.onAuctionEnded.bind(auctionSocketService),
    onAuctionCancelled: auctionSocketService.onAuctionCancelled.bind(auctionSocketService),
    onAuctionStatusUpdate: auctionSocketService.onAuctionStatusUpdate.bind(auctionSocketService),
    onBuyNowCompleted: auctionSocketService.onBuyNowCompleted.bind(auctionSocketService),
    onTimeWarning: auctionSocketService.onTimeWarning.bind(auctionSocketService),
  }
}
